// @flow
import { connect } from 'react-redux'
import type { Store } from '../../redux/ducks'
import AssignmentForm from './AssignmentForm'
import { put, patch } from '../../redux/ducks/advertisementTemplatesAssignment/reducers'
import _ from 'lodash'
import type { Initial } from './AssignmentForm'
import store from '../../redux/store'
import postJson from '../../services/postJson'
import { patch as audienceSelectorPatch } from '../../redux/ducks/audiencesSelector/reducers'
import type { State as AssignmentState } from '../../redux/ducks/advertisementTemplatesAssignment/reducers'
import type { OptionType } from '../types'

type ErrorsResponseData = {|
  audience_ids?: Array<string>,
  advertisement_template_ids?: Array<string>
|}

type Payload = {|
  data: {|
    audience_ids: Array<number>,
    advertisement_template_ids: Array<number>
  |}
|}

function mapStateToProps(store: Store) {
  const assignmentState = store.advertisementTemplateAssignment

  return {
    advertisementTemplateOptions: toOptions(assignmentState.advertisementTemplates),
    advertisementTemplateIds: assignmentState.advertisementTemplateIds,
    advertisementTemplateIdsErrors: assignmentState.advertisementTemplateIdsErrors,
    showRequestButton: !_.isEmpty(assignmentState.advertisementTemplateIds),
    isLoading: assignmentState.isLoading,
    notMatchedTemplates: notMatchedTemplates(store)
  }
}

function notMatchedTemplates(store: Store) {
  const audienceIds = store.audiencesSelector.ids
  const assignmentState = store.advertisementTemplateAssignment
  const selectedTemplateIds = assignmentState.advertisementTemplateIds
  const audiences = assignmentState.audiences
  if (audienceIds.length === 0) return []
  const audienceBasedList = _.reduce(
    audiences,
    (result, audience) =>
      _.union(result, _.includes(audienceIds, audience.id) ? audience.list : []),
    []
  )

  const notMatched = _.filter(
    assignmentState.advertisementTemplates,
    (template) =>
      _.includes(selectedTemplateIds, template.id) &&
      !_.isEmpty(_.difference(audienceBasedList, template.list))
  )

  return _.map(notMatched, (template) => ({
    name: optionName(template),
    fixUrl: `/advertisement_templates/${template.id}/edit/?audience_ids=${audienceIds.join(',')}`
  }))
}

function toOptions(items: Array<Object>): Array<OptionType> {
  return _.map(items, (item) => ({ id: item.id, name: optionName(item) }))
}

function optionName(object: Object) {
  return `[#${object.id}] ${object.name}`
}

const assignmentApiUrl = '/api/v1/advertisement_template_assignments'

function mapDispatchToProps(dispatch: Function) {
  return {
    onChange: (diff) => dispatch(patch(diff)),
    onPersist: (callbacks: {| onSuccess: () => void, onFailure: () => void |}) => {
      dispatch(patch({ isLoading: true }))
      const state: Store = store.getState()
      postJson(assignmentApiUrl, requestBody(state))
        .done(() => {
          dispatch(patch({ advertisementTemplateIdsErrors: [] }))
          dispatch(audienceSelectorPatch({ errors: [] }))
          callbacks.onSuccess()
        })
        .fail((response: any) => {
          if (response.status === 500) {
            window.alert('Internal Server Error. Contact support')
          } else {
            const errorsResponse: ErrorsResponseData = response.responseJSON.message
            dispatch(audienceSelectorPatch({ errors: errorsResponse.audience_ids || [] }))
            dispatch(patch(processErrors(errorsResponse)))
          }
          callbacks.onFailure()
        })
        .always(() => {
          dispatch(patch({ isLoading: false }))
        })
    }
  }
}

function processErrors(errors: ErrorsResponseData): $Shape<AssignmentState> {
  return {
    advertisementTemplateIdsErrors: errors.advertisement_template_ids || []
  }
}

export function initialize(dispatch: Function, initial: Initial) {
  dispatch(
    put({
      isLoading: false,
      advertisementTemplateIds: [],
      advertisementTemplates: initial.advertisement_templates,
      audiences: initial.audiences,
      advertisementTemplateIdsErrors: []
    })
  )
}

function requestBody(store: Store): Payload {
  return {
    data: {
      audience_ids: store.audiencesSelector.ids,
      advertisement_template_ids: store.advertisementTemplateAssignment.advertisementTemplateIds
    }
  }
}

const AssignmentFormContainer = connect<Object, Object, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(AssignmentForm)

export default AssignmentFormContainer
